import _ from 'lodash';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Avatar, Button, Card, CardHeader, CardBody } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { useGetSurveyQuery } from '@/patient-app-common/api/surveyApi';
import useSurveyDrafts from '@/hooks/useSurveyDrafts';

import { DailyResult } from '../programs/surgical/DailyResult';

export function TrackerEventList() {
  const { isLoading, activeTrackers } = useAppEvents();

  if (isLoading) return null;

  return (
    <div className="gap-4">
      {activeTrackers.map((event) => (
        <TrackerEvent event={event} key={event.id} />
      ))}
    </div>
  );
}

function TrackerEvent({ event }) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const { data: survey } = useGetSurveyQuery(event.options.id, {
    skip: !event.options.id,
  });
  const { surveyDrafts } = useSurveyDrafts();

  const entryFromToday = useMemo(() => {
    if (!survey) return false;

    const { lastEntryDate } = survey;

    if (lastEntryDate) {
      return dayjs().isSame(lastEntryDate, 'd');
    }
    return false;
  }, [survey]);

  const hasDraft = useMemo(() => {
    if (!surveyDrafts?.length) return false;

    const draft = _.find(surveyDrafts, {
      procedureId: _.toNumber(event.options.id),
    });

    const draftDate = draft?.draftCreatedAt;

    if (!draftDate) return false;

    const draftFromToday = dayjs()
      .startOf('day')
      .isSame(dayjs(draftDate).startOf('day'));

    return draftFromToday;
  }, [event, surveyDrafts]);

  const btnText = hasDraft
    ? t('common.button.survey_edit')
    : t('common.button.survey_start');

  if (entryFromToday) {
    return <DailyResult mainTrackerId={event.options.id} />;
  }

  return (
    <Card className="bg-primary-100" radius="sm" shadow="sm">
      <CardHeader className="flex gap-4">
        <Avatar
          color="primary"
          icon={
            <Icon
              icon="material-symbols:health-and-safety-outline"
              className="text-3xl text-background"
            />
          }
          size="md"
        />
        <h2 className="font-semibold">{event.title}</h2>
      </CardHeader>
      <CardBody className="pt-0">
        <p className="px-8 whitespace-pre-line">{event.body}</p>
        <Button
          className="mt-4 min-w-1/4 place-self-end"
          color="primary"
          onPress={() =>
            navigate(`/tracker/${event.options.id}`, {
              state: { eventId: event.id },
            })
          }
        >
          {btnText}
        </Button>
        {hasDraft && (
          <div className="text-right font-light">
            <p className="inline-flex text-sm gap-1 text-right">
              <Icon icon="mdi:pencil" className="place-self-center" />
              <span>{t('common.text.has_draft')}</span>
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
